import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Send, CheckCircle2, ArrowRight, Home } from 'lucide-react';
import { toast } from 'sonner';
import { todayIso } from '@/lib/dateRange';
import { RangeDatePicker } from '@/components/common/DatePickers';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Lead capture entry-point for the public site.
 *
 * Two presentation modes, controlled by `mode`:
 *   - "cta" (default) → renders the "Ready for Miami?" link on the listings
 *      page that opens the dialog.
 *   - "inline" → renders nothing visible; the parent supplies its own button
 *      and controls `open` via state. Used by the listing detail page so the
 *      contact CTA sits naturally next to the listing's own info.
 *
 * When opened from a specific listing, pass a `listing={{ id, title }}` so
 * the form tags the lead. The team's inbox + email both surface the unit
 * contacted from.
 */
export function LeadCaptureForm({ mode = 'cta', listing = null, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = mode === 'inline' ? !!controlledOpen : internalOpen;
  const setOpen = mode === 'inline' ? (onOpenChange || (() => {})) : setInternalOpen;

  const [form, setForm] = useState({ name: '', email: '', move_in: '', move_out: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          move_in: form.move_in || null,
          move_out: form.move_out || null,
          message: form.message.trim() || null,
          listing_id: listing?.id || null,
          listing_name: listing?.title || null,
        }),
      });
      if (res.ok) {
        setDone(true);
        toast.success('Thanks — we\'ll be in touch soon!');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to send. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setForm({ name: '', email: '', move_in: '', move_out: '', message: '' });
    }, 250);
  };

  const dialog = (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); else setOpen(true); }}>
      <DialogContent className="max-w-lg" data-testid="lead-form-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="lead-form-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-heading text-xl font-semibold tracking-tight text-[#064e3b]">Message received</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Thanks, {form.name.split(' ')[0] || 'there'}! We'll email you shortly at <span className="font-medium text-foreground">{form.email}</span>.
            </p>
            <Button className="mt-6" onClick={resetAndClose} data-testid="lead-form-done-close">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight">
                {listing ? (
                  <span className="flex items-center gap-2"><Home className="h-5 w-5 text-emerald-700" />Interested in {listing.title}?</span>
                ) : (
                  "Share what you're looking for and we'll match you with options."
                )}
              </DialogTitle>
              {listing && (
                <DialogDescription>
                  Send us a quick note and we'll get back to you about this unit.
                </DialogDescription>
              )}
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="lead-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Name</Label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Doe" required data-testid="lead-form-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Email</Label>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" required data-testid="lead-form-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Dates (optional)</Label>
                  <RangeDatePicker
                    start={form.move_in}
                    end={form.move_out}
                    onChange={({ start, end }) => setForm(f => ({ ...f, move_in: start, move_out: end }))}
                    minIso={todayIso()}
                    testId="lead-form-dates"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Message (optional)</Label>
                <textarea
                  value={form.message}
                  onChange={e => set('message', e.target.value)}
                  placeholder={listing ? "Anything we should know? Move-in flexibility, parking, pets…" : "Tell us what you're looking for — size, neighborhood, must-haves…"}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                  data-testid="lead-form-message"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto" data-testid="lead-form-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                {submitting ? 'Sending…' : 'Send message'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  if (mode === 'inline') return dialog;

  return (
    <>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 text-center" data-testid="lead-form-cta-section">
        <button
          onClick={() => setOpen(true)}
          className="group inline-flex items-center gap-2 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#064e3b] hover:text-[#047857] transition-colors"
          data-testid="lead-form-cta"
        >
          Ready for Miami? <span className="underline underline-offset-4 decoration-2 decoration-emerald-300 group-hover:decoration-emerald-500">Contact us here</span>
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </button>
      </section>
      {dialog}
    </>
  );
}
